import { useEffect, useState } from 'react'

import { Box, Grid, Link } from '@factorialco/deprecated-design-system'
import { Alert, AlertDescription, AlertTitle } from '@factorialco/f0-react/dist/experimental'
import { BaseTabs, ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { ProjectWorkersPanelDocument } from 'generated/resources/graphql'
import {
  CreateProjectManagementRateDocument,
  CreateProjectManagementRateMutation,
  ProjectPeopleDataCollectionDocument,
  UpdateProjectManagementRateDocument,
  UpdateProjectManagementRateMutation,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import Loading from 'modules/layout/components/Page/Loading'
import CostHistoryRecords from 'modules/project_management/components/CostHistoryRecords'
import { InsightCard } from 'modules/project_management/components/InsightCard'
import EditRateCard from 'modules/project_management/components/ProfitabilityCards/EditRateCard'
import { ProjectWorkersPanelData } from 'modules/project_management/components/tables/ProjectPeopleDataCollection/types'
import { convertProfitabilityToCents } from 'modules/project_management/lib/profitabilityCalculations'

export interface ProjectWorkersSidepanelProps {
  projectId: number
  data: ProjectWorkersPanelData
  isIndividualRateType: boolean
  onClose: () => void
  showProjectWorkersPanel: boolean
}

const ProjectWorkersSidepanel = ({
  data,
  projectId,
  isIndividualRateType,
  onClose,
  showProjectWorkersPanel,
}: ProjectWorkersSidepanelProps) => {
  const { navigation } = useNavigation()
  const [activeTab, setActiveTab] = useState<'cost-history' | 'billing-rate'>('cost-history')

  const [updateProjectWorkerRate] = useMutation(UpdateProjectManagementRateDocument, {
    extractorFn: (mutationData: UpdateProjectManagementRateMutation) =>
      mutationData.projectManagementMutations.updateProjectmanagementRate,
    awaitRefetchQueries: true,
    refetchQueries: [ProjectPeopleDataCollectionDocument],
  })

  const [createProjectWorkerRate] = useMutation(CreateProjectManagementRateDocument, {
    extractorFn: (mutationData: CreateProjectManagementRateMutation) =>
      mutationData.projectManagementMutations.createProjectmanagementRate,
    awaitRefetchQueries: true,
    refetchQueries: [ProjectPeopleDataCollectionDocument],
  })

  useEffect(() => {
    if (showProjectWorkersPanel) {
      setActiveTab('cost-history')
    }
  }, [showProjectWorkersPanel])

  const {
    projectWorkerId,
    billingRateCents,
    chargingCostCents,
    profitabilityPercentageCents,
    referenceBillingRateCents,
    referenceProfitMarginCents,
    ratesCurrency,
    rateId,
    employeeAccessAvatar,
    employeeFullName,
    imputedMinutes,
    laborCostCents,
    spendingCostCents,
    projectCurrency,
    canSeeLaborCosts,
    canSeeRates,
    canSeeSpendings,
  } = data

  const { data: costHistoryData, loading: loadingCostHistoryRecords } = useQuery(
    ProjectWorkersPanelDocument,
    {
      variables: {
        projectWorkerId,
      },
    }
  )

  const costHistoryRecords =
    costHistoryData?.projectManagement.projectWorkerCostHistoryRecords || []

  const handleUpdateProjectWorkerRate = async (values: {
    billRate: string
    minProfitMargin: string
  }) => {
    track(trackingEvents.projectManagement.jobsAndRates.clickOnSaveIndividualBillRate)

    const newBillingRateCents = values.billRate ? Math.round(Number(values.billRate) * 100) : null

    const newProfitabilityPercentageCents = values.minProfitMargin
      ? convertProfitabilityToCents(Number(values.minProfitMargin))
      : null

    if (rateId) {
      const response = await updateProjectWorkerRate({
        variables: {
          id: rateId,
          billingRateCents: newBillingRateCents,
          profitabilityPercentageCents: newProfitabilityPercentageCents,
        },
      })
      const handled = handleResult(response, () => undefined)

      app.setFlash({
        type: handled.success ? 'correct' : 'wrong',
        message: handled.success
          ? i18n.t('common.saved_successfully')
          : i18n.t('errors.generic_saving'),
      })
    } else {
      const response = await createProjectWorkerRate({
        variables: {
          billingRateCents: newBillingRateCents || 0,
          profitabilityPercentageCents: newProfitabilityPercentageCents || 0,
          currency: ratesCurrency || projectCurrency,
          projectId: projectId,
          resourceId: projectWorkerId,
          resourceType: 'ProjectManagement::ProjectWorker',
        },
      })
      const handled = handleResult(response, () => undefined)

      app.setFlash({
        type: handled.success ? 'correct' : 'wrong',
        message: handled.success
          ? i18n.t('common.saved_successfully')
          : i18n.t('errors.generic_saving'),
      })
    }

    onClose()
  }

  return (
    <Sidepanel
      boxPadding={false}
      title={i18n.t('project_management.project_workers.details.cost_history')}
      open={showProjectWorkersPanel}
      onClose={onClose}
      width={'wide'}
    >
      <Box flexDirection="row" alignItems="center">
        <ResourceHeader
          avatar={{
            type: 'person',
            firstName: employeeFullName || '',
            lastName: '',
            src: buildUrl(employeeAccessAvatar),
          }}
          title={employeeFullName || ''}
          metadata={
            !!chargingCostCents
              ? undefined
              : [
                  {
                    label: i18n.t('project_management.headers.cost_rate'),
                    value: {
                      type: 'status' as const,
                      label: i18n.t(
                        'project_management.project_workers.details.cost_missing.title'
                      ),
                      variant: 'info' as const,
                    },
                    info: {
                      title: i18n.t(
                        'project_management.project_workers.details.cost_missing.tooltip_message'
                      ),
                    },
                  },
                ]
          }
        />
      </Box>

      {isIndividualRateType && canSeeRates && (
        <BaseTabs
          tabs={[
            {
              id: 'cost-history',
              index: true,
              label: i18n.t('project_management.project_workers.details.cost_history'),
              onClick: () => setActiveTab('cost-history'),
            },
            {
              id: 'billing-rate',
              label: i18n.t('project_management.project_workers.details.billing_rate'),
              onClick: () => setActiveTab('billing-rate'),
            },
          ]}
        />
      )}
      {((activeTab === 'cost-history' && isIndividualRateType) || !isIndividualRateType) && (
        <Box paddingX="s24">
          <Box paddingTop="s16" paddingBottom="s32" gap="s16">
            <Grid variant="fixed">
              {canSeeLaborCosts && (
                <Box gridColSpan={{ xxs: 6 }}>
                  <InsightCard.Cost
                    totalCost={laborCostCents || 0}
                    currency={projectCurrency}
                    title={i18n.t('project_management.project_workers.details.labor_cost')}
                    titleGap="s2"
                  />
                </Box>
              )}
              {canSeeRates && (
                <Box gridColSpan={{ xxs: 6 }}>
                  <InsightCard.Cost
                    totalCost={Math.round(((imputedMinutes || 0) / 60) * (billingRateCents || 0))}
                    currency={projectCurrency}
                    title={i18n.t('project_management.project_workers.details.labor_bill')}
                    titleGap="s2"
                  />
                </Box>
              )}
              <Box gridColSpan={{ xxs: 6 }}>
                <InsightCard.Hours
                  imputedMinutes={imputedMinutes || 0}
                  title={i18n.t('project_management.project_workers.details.registered_hours')}
                  titleGap="s2"
                />
              </Box>
              {canSeeSpendings && (
                <Box gridColSpan={{ xxs: 6 }}>
                  <InsightCard.Cost
                    totalCost={spendingCostCents || 0}
                    currency={projectCurrency}
                    title={i18n.t('project_management.project_workers.details.spending_cost')}
                    titleGap="s2"
                  />
                </Box>
              )}
            </Grid>
          </Box>
          {loadingCostHistoryRecords ? (
            <Loading />
          ) : (
            <Box flexDirection="row" width="full" paddingTop="s16">
              <CostHistoryRecords
                costHistoryRecords={costHistoryRecords}
                currency={projectCurrency}
              />
            </Box>
          )}
        </Box>
      )}
      {activeTab === 'billing-rate' && isIndividualRateType && canSeeRates && (
        <Box padding="s24" gap="s20">
          {!billingRateCents && (
            <Alert variant="warning">
              <AlertTitle>
                {i18n.t(
                  'project_management.people_and_rates.sidepanel.alerts.configure_bill_rate.title'
                )}
              </AlertTitle>
              <AlertDescription>
                {i18n.t(
                  'project_management.people_and_rates.sidepanel.alerts.configure_bill_rate.description'
                )}
              </AlertDescription>
              <Link.Router
                type="solid"
                to={`${navigation.projects.jobsAndRates.list.fullPath()}?openAddSidepanel=true`}
              >
                {i18n.t(
                  'project_management.people_and_rates.sidepanel.alerts.configure_bill_rate.link'
                )}
              </Link.Router>
            </Alert>
          )}
          <EditRateCard
            areProjectWorkerRates
            billingRateCents={(billingRateCents || 0) / 100}
            currency={ratesCurrency || projectCurrency}
            referenceBillingRate={
              referenceBillingRateCents ? referenceBillingRateCents / 100 : undefined
            }
            referenceProfitMargin={
              referenceProfitMarginCents ? referenceProfitMarginCents / 100 : undefined
            }
            maxCostRate={(chargingCostCents || 0) / 100}
            initialProfitMargin={profitabilityPercentageCents || 0}
            onSubmit={(values) => {
              handleUpdateProjectWorkerRate(values)
            }}
          />
        </Box>
      )}
    </Sidepanel>
  )
}

export default observer(ProjectWorkersSidepanel)
